import Grid from '@mui/material/Grid2';
import { Box, Skeleton, Stack } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useEffect, useRef } from 'react';
import NoteHeader from './NoteHeader';
import TeamNoteBreadcrumb from './TeamNoteBreadcrumb';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import { GridContainer } from '../styles';
import Summary from './Summary/Summary';
import RightTabs from './RightTabs';
import AudioPlayer from './AudioPlayer/AudioPlayer';
import AudioChapters from './AudioPlayer/AudioChapters';
import VideoChapters from './VideoPlayer/VideoChapters';
import { InitialAssessment, NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import SummaryFeedback, { SummaryFeedbackValue } from './SummaryFeedback/SummaryFeedback';
import { DocumentChapter, Transcript } from '@/types/note';
import UpgradePrompt from '@/components/UpgradePrompt';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { FirstNoteBadge } from '@/features/genesisEvent';
import { logger } from '@/services/loggerService';
import ContentAnalysis from './DocumentChapters/ContentAnalysis';
import useFetchContentAnalysis from '@/hooks/useFetchContentAnalysis';
import NoteContentTabs from './NoteContentTabs';
import { hasBetaFeature } from '@/utils/subscriptionHelpers';
import { useAuthStore } from '@/stores/authStore';
import { useSendToZapier } from '@/features/zapier/hooks/useSendToZapier';
import type { UserWithZapier } from '@/features/zapier/types';
import audioThumbnail from '@/assets/audio/audio_thumbnail.jpg';
import SilentErrorBoundary from '@/components/SilentErrorBoundary';
import DesktopAppPromo from '@/pages/Note/components/DesktopAppPromo';

type NoteLayoutProps = {
  isMobile: boolean;
  noteTitle: string;
  noteSummary: string;
  noteDate: string;
  isOwner: boolean;
  isProcessing: boolean;
  isNoteProcessing: boolean;
  processingTitleVisible: boolean;
  isLoadingNote: boolean;
  onTitleUpdate: (newTitle: string) => void;
  noteData: NoteOut | null;
  shouldShowShareArrow: boolean;
  signedUrl: string;
  signedThumbnailUrl?: string;
  isAudioOnly: boolean;
  trackPlay?: () => void;
  isLoadingTranscript: boolean;
  isLoadingFullTranscript: boolean;
  fullTranscriptList: Transcript[] | null;
  refetchNote: () => Promise<unknown>;
  onDocumentChaptersUpdate: (chapters: DocumentChapter[]) => Promise<void>;
  onKeyPointsUpdate: (keyPoints: Transcript[]) => void;
  onContentAnalysisUpdate: (assessment: InitialAssessment) => Promise<void>;
  showSummaryFeedback: boolean;
  noteId?: string;
  validatedSummaryFeedback: SummaryFeedbackValue;
  onFeedbackChange: (value: SummaryFeedbackValue) => void;
};

const NoteLayout = ({
  isMobile,
  noteTitle,
  noteSummary,
  noteDate,
  isOwner,
  isProcessing,
  isNoteProcessing,
  processingTitleVisible,
  isLoadingNote,
  onTitleUpdate,
  noteData,
  shouldShowShareArrow,
  signedUrl,
  signedThumbnailUrl,
  isAudioOnly,
  trackPlay,
  isLoadingTranscript,
  isLoadingFullTranscript,
  fullTranscriptList,
  refetchNote,
  onDocumentChaptersUpdate,
  onKeyPointsUpdate,
  onContentAnalysisUpdate,
  showSummaryFeedback,
  noteId,
  validatedSummaryFeedback,
  onFeedbackChange,
}: NoteLayoutProps) => {
  // Video loading instrumentation — log every render decision
  const layoutRenderCount = useRef(0);
  layoutRenderCount.current += 1;
  const renderN = layoutRenderCount.current;
  logger.debug('video', `NoteLayout render #${renderN}`, {
    hasSignedUrl: Boolean(signedUrl),
    signedUrlLength: signedUrl?.length ?? 0,
    hasPosterUrl: Boolean(signedThumbnailUrl),
    isProcessing,
    isAudioOnly,
    isLoadingNote,
    rendering: signedUrl ? 'VideoPlayer' : 'Skeleton',
  });

  const { features, betaFeatures, isLoading: isFeaturesLoading } = useCurrentPlanFeatures();
  const { userData } = useAuthStore();
  const { sendToZapier, isSending: isSendingToZapier } = useSendToZapier();
  const showZapierButton =
    isOwner &&
    hasBetaFeature(userData, betaFeatures, 'zapier') &&
    !!(userData as UserWithZapier)?.zapier_webhook_url &&
    !isNoteProcessing;
  // Built only from the current note's own payload: `isOwner` and the processing
  // flag are reset in effects, so on a note-to-note navigation they still describe
  // the previous note for a pass or two — long enough for the promo to latch on a
  // note that has already finished.
  const isDesktopPromoEligible =
    !!noteData &&
    noteData.id === noteId &&
    !!userData?.id &&
    userData.id === noteData.creator_user_id &&
    !noteData.processing_type &&
    isNoteProcessing &&
    !isLoadingNote &&
    !isFeaturesLoading &&
    features?.desktopDownload === true;
  const handleSendToZapier = () => {
    if (noteId) void sendToZapier(noteId);
  };

  useEffect(() => {
    logger.debug('notes', 'NoteLayout chapters visibility state', {
      isFeaturesLoading,
      aiChaptersEnabled: features?.aiChapters ?? false,
      isProcessing,
      isAudioOnly,
      documentChapterCount: noteData?.document_chapter?.length ?? 0,
      keyMomentsCount: noteData?.key_moments?.length ?? 0,
    });
  }, [
    features?.aiChapters,
    isAudioOnly,
    isFeaturesLoading,
    isProcessing,
    noteData?.document_chapter?.length,
    noteData?.key_moments?.length,
  ]);

  const renderChaptersSection = () => {
    const ChaptersSkeleton = (
      <Box sx={{ mt: 4, maxWidth: '800px', width: '100%' }}>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      </Box>
    );

    if (isFeaturesLoading) {
      return ChaptersSkeleton;
    }

    if (!features?.aiChapters) {
      return (
        <Box sx={{ mt: 4, maxWidth: '800px', width: '100%' }}>
          <UpgradePrompt featureName="AI Chapters" description="AI-generated chapter markers are an upgraded feature" />
        </Box>
      );
    }

    if (!noteData?.document_chapter) {
      return isProcessing ? ChaptersSkeleton : null;
    }

    return isAudioOnly ? (
      <AudioChapters
        chapters={noteData.document_chapter}
        isProcessing={isProcessing}
        isOwner={isOwner}
        onChaptersUpdate={onDocumentChaptersUpdate}
      />
    ) : (
      <VideoChapters
        chapters={noteData.document_chapter}
        isProcessing={isProcessing}
        isOwner={isOwner}
        onChaptersUpdate={onDocumentChaptersUpdate}
      />
    );
  };

  const { initialAssessment } = useFetchContentAnalysis(
    noteData?.content_analysis,
    noteData?.full_transcript_url || noteData?.transcript_url,
  );

  return (
    <>
      <Helmet>
        <title>{noteTitle}</title>
        <meta name="description" content={noteSummary} />
        <meta property="og:title" content={noteTitle} />
        <meta property="og:description" content={noteSummary} />
        <meta name="twitter:title" content={noteTitle} />
        <meta name="twitter:description" content={noteSummary} />
      </Helmet>
      <Stack alignItems={isMobile ? 'flex-start' : 'center'} margin={isMobile ? '8px' : '32px'}>
        <TeamNoteBreadcrumb noteId={noteId} />
        <NoteHeader
          title={noteTitle}
          date={noteDate}
          isOwner={isOwner}
          isProcessing={isNoteProcessing}
          processingTitleVisible={processingTitleVisible}
          isLoadingNote={isLoadingNote}
          onTitleUpdate={onTitleUpdate}
          note={noteData}
          shouldShowShareArrow={shouldShowShareArrow}
          sharedBy={
            !isOwner && noteData?.shared_by && noteData.shared_by.first_name
              ? {
                  name: `${noteData.shared_by.first_name || ''} ${noteData.shared_by.last_name || ''}`.trim(),
                  email: noteData.shared_by.email || undefined,
                }
              : null
          }
          showZapierButton={showZapierButton}
          onSendToZapier={handleSendToZapier}
          isSendingToZapier={isSendingToZapier}
        />
        <GridContainer container spacing={{ xs: 0, lg: 3 }}>
          {/* Main video and summary column */}
          <Grid
            size={{ xs: 12, lg: 8.4 }}
            sx={{
              '@media (max-width: 600px)': {
                paddingRight: '0 !important',
                paddingLeft: '0 !important',
                width: '100%',
                '& > *': {
                  maxWidth: '100%',
                },
              },
            }}
          >
            {signedUrl ? (
              <Box sx={{ position: 'relative', maxWidth: isMobile ? '100%' : '800px' }}>
                {/* First Note Badge - shows permanently for the user's first note */}
                {isOwner && <FirstNoteBadge noteId={noteId} position="top-right" />}
                {isAudioOnly ? (
                  <AudioPlayer
                    src={signedUrl}
                    chapters={noteData?.document_chapter}
                    audioReady={noteData?.audio_ready ?? true}
                    isProcessing={isProcessing}
                  />
                ) : (
                  <VideoPlayer
                    src={signedUrl}
                    chapters={noteData?.document_chapter}
                    isMobile={isMobile}
                    videoReady={noteData?.video_ready ?? true}
                    isTrimEnabled={noteData?.processing_params?.trim?.enabled ?? false}
                    isProcessing={isProcessing}
                    onPlay={trackPlay}
                    posterUrl={signedThumbnailUrl}
                    videoWidth={noteData?.metadata?.media?.webm?.width}
                    videoHeight={noteData?.metadata?.media?.webm?.height}
                    noteId={noteId}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  maxWidth: isMobile ? '100%' : '800px',
                  borderRadius: isMobile ? '12px' : '22px',
                  overflow: 'hidden',
                  ...(isAudioOnly && {
                    backgroundImage: `url(${audioThumbnail})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }),
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  sx={{
                    aspectRatio:
                      noteData?.metadata?.media?.webm?.width && noteData?.metadata?.media?.webm?.height
                        ? `${noteData.metadata.media.webm.width} / ${noteData.metadata.media.webm.height}`
                        : '16 / 9',
                    height: 'auto',
                    maxHeight: isMobile ? '60vh' : '70vh',
                  }}
                />
              </Box>
            )}
            {/* Keyed by note so a promo shown on one note never carries over to the next. */}
            <SilentErrorBoundary feature="notes" operation="desktopAppPromo">
              <DesktopAppPromo key={noteId} eligible={isDesktopPromoEligible} />
            </SilentErrorBoundary>
            <NoteContentTabs>
              <Summary
                content={noteSummary}
                isOwner={isOwner}
                isProcessing={isNoteProcessing}
                isLoadingNote={isLoadingNote}
                rawSummary={noteData?.summary}
              />
              {renderChaptersSection()}
              {initialAssessment && (
                <ContentAnalysis
                  initialAssessment={initialAssessment}
                  isOwner={isOwner}
                  isProcessing={isNoteProcessing}
                  isLoadingNote={isLoadingNote}
                  onUpdate={onContentAnalysisUpdate}
                />
              )}
            </NoteContentTabs>
          </Grid>
          {/* Sidebar */}
          <Grid
            size={{ xs: 12, lg: 3.6 }}
            sx={{
              // add small top margin when stacked on tablet/mobile
              mt: { xs: 3, lg: 0 },
              '@media (max-width: 600px)': {
                paddingLeft: '0 !important',
              },
            }}
          >
            <RightTabs
              tags={noteData?.tags || []}
              isAudioOnly={isAudioOnly}
              isOwner={isOwner}
              isProcessing={isProcessing}
              isNoteProcessing={isNoteProcessing}
              isLoading={isLoadingNote || isLoadingTranscript || isLoadingFullTranscript}
              fullTranscriptList={fullTranscriptList}
              keyMoments={noteData?.key_moments || null}
              refetchNote={refetchNote}
              noteId={noteId}
              fullTranscriptUrl={noteData?.full_transcript_url || null}
              onKeyPointsUpdate={onKeyPointsUpdate}
            />
          </Grid>
        </GridContainer>
        {showSummaryFeedback && noteId && (
          <Box width="100%" mt={4} display="flex" justifyContent={isMobile ? 'flex-start' : 'center'}>
            <SummaryFeedback noteId={noteId} value={validatedSummaryFeedback} onChange={onFeedbackChange} />
          </Box>
        )}
      </Stack>
    </>
  );
};

export default NoteLayout;
